import React from 'react'
import { Avatar, Box, Chip, Paper, Stack, Typography, useTheme } from '@mui/material'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import CheckIcon from '@mui/icons-material/Check'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import LinkIcon from '@mui/icons-material/Link'
import {
  STATUS_META,
  PRIORITY_META,
  getDueMeta,
  getLinkedRecord,
  getTeamColorKey,
  isAssignedToMe,
  resolveColor,
} from './taskHelpers'

const Dot = ({ color, size = 8 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      bgcolor: color,
      flexShrink: 0,
    }}
  />
)

// One task on the board. Purely presentational — the board decides how it is
// dragged and what happens on click.
const TaskCard = ({ task, currentUserId, onClick, dragging, sx }) => {
  const theme = useTheme()
  const statusColor = resolveColor(theme, STATUS_META[task.taskStatus].colorKey)
  const priorityColor = resolveColor(
    theme,
    PRIORITY_META[task.taskPriority].colorKey,
  )
  const due = getDueMeta(task)
  const linked = getLinkedRecord(task)
  const completed = task.taskStatus === 'Completed'
  const mine = isAssignedToMe(task, currentUserId)
  const commentCount = task.comments?.length ?? 0

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 1.25,
        pl: 1.5,
        cursor: 'pointer',
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: '4px solid',
        borderLeftColor: statusColor,
        bgcolor: 'background.paper',
        opacity: completed ? 0.75 : 1,
        transition:
          'border-color 150ms ease, transform 150ms ease, box-shadow 150ms ease',
        '&:hover': {
          boxShadow: '0 8px 18px rgba(15, 23, 42, 0.10)',
          transform: 'translateY(-1px)',
        },
        ...(dragging && {
          boxShadow: '0 12px 28px rgba(15, 23, 42, 0.18)',
          transform: 'rotate(1.5deg)',
        }),
        ...sx,
      }}
    >
      <Stack gap={1}>
        <Typography
          variant="body2"
          sx={{
            color: 'text.primary',
            fontWeight: 600,
            lineHeight: 1.4,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {task.taskName}
        </Typography>

        {linked && (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <LinkIcon sx={{ fontSize: 14 }} />
            {linked.type} · {linked.label}
          </Typography>
        )}

        <Stack direction="row" gap={0.75} flexWrap="wrap">
          {completed ? (
            <Chip
              size="small"
              variant="outlined"
              color="success"
              icon={<CheckIcon />}
              label={`Done ${
                task.updatedAt
                  ? new Date(task.updatedAt).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                    })
                  : ''
              }`}
              sx={{ borderRadius: 1 }}
            />
          ) : (
            <>
              <Chip
                size="small"
                variant="outlined"
                icon={<Dot color={priorityColor} />}
                label={task.taskPriority}
                sx={{ borderRadius: 1, pl: 0.75, color: 'text.secondary' }}
              />
              {task.dueDate && (
                <Chip
                  size="small"
                  variant="outlined"
                  color={due.color}
                  icon={<CalendarTodayIcon />}
                  label={due.label}
                  sx={{
                    borderRadius: 1,
                    ...(due.color === 'default' && { color: 'text.secondary' }),
                  }}
                />
              )}
            </>
          )}
        </Stack>

        <Stack direction="row" alignItems="center" gap={1}>
          {task.assignedToUser ? (
            <>
              <Avatar
                src={task.assignedToUser.photograph}
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: 1,
                  fontSize: 11,
                  bgcolor: 'contrast.main',
                }}
              >
                {task.assignedToUser.firstName?.[0]}
                {task.assignedToUser.lastName?.[0]}
              </Avatar>
              <Typography variant="caption" color="text.primary" noWrap>
                {mine
                  ? 'Me'
                  : `${task.assignedToUser.firstName} ${task.assignedToUser.lastName}`}
              </Typography>
            </>
          ) : (
            task.assignedToTeam && (
              <Chip
                size="small"
                variant="outlined"
                icon={
                  <Dot
                    color={resolveColor(
                      theme,
                      getTeamColorKey(task.assignedToTeam),
                    )}
                  />
                }
                label={task.assignedToTeam}
                sx={{ borderRadius: 1, pl: 0.75, color: 'text.secondary' }}
              />
            )
          )}
          {commentCount > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <ChatBubbleOutlineIcon sx={{ fontSize: 15 }} />
              {commentCount}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Paper>
  )
}

export default TaskCard
